/**
 * Standort-Detail-Ansicht (Design-Canvas "ShiftSync Rollenansichten",
 * Artboard StandortDetail.dc.html) — Nachtrag 08.09.2026.
 *
 * Auslöser: Shadow — beim Klick auf eine Standort-Kachel im Chef-Dashboard
 * landete man bisher wieder auf der ALLGEMEINEN Standort-Liste
 * (`/admin/locations`, alle Standorte) statt auf einer Detail-Ansicht GENAU
 * dieses einen Standorts mit Mitarbeitern usw. — wie im Artefakt gezeigt.
 * Diese Seite schließt die Lücke: erreichbar über `/admin/locations/:id`,
 * verlinkt von DashboardPage (Chef-Standortkarten).
 *
 * Zugriff: Owner ODER Filialleitung GENAU an diesem Standort
 * (`useCanManageLocation`) — spiegelt `hasLocationAccess`/`isLocationManager`
 * im Backend. Die Mitarbeiter-/Lohn-Daten kommen über die BESTEHENDEN,
 * bereits maskierten Endpunkte (`GET /api/users`, `GET /api/shifts`) — diese
 * Seite erfindet keine neue Sichtbarkeits-Logik, sie aggregiert nur.
 *
 * Bewusste Vereinfachungen ggü. dem Mockup:
 *  - "Vorlagen verwalten" verlinkt auf die bestehende Standort-Liste
 *    (`/admin/locations`), wo die Vorlagen dieses Standorts bereits pflegbar
 *    sind (ausklappbare Karte) — keine zweite Vorlagen-Verwaltung gebaut.
 *
 * Nachtrag 08.09.2026 (2. Feedback-Runde, Deadline 19 Uhr):
 *  - Chef/Admins werden aus der Mitarbeiter-Liste ausgefiltert (waren nie als
 *    "Mitarbeiter" gemeint).
 *  - "Schichtplan" springt jetzt direkt in den Wochenplan DIESES Standorts
 *    (`/wochenplan?location=:id`, siehe WeeklyPlanPage Deep-Link-Support).
 *  - "+ anlegen" öffnet den bestehenden `UserFormModal` (aus UsersAdminPage,
 *    jetzt exportiert) inline statt wegzunavigieren, mit `defaultLocationId`.
 *  - Mitarbeiter-Zeilen sind ausklappbar und zeigen die Stunden pro Tag der
 *    aktuellen Woche (aus den ohnehin geladenen `weekShifts`).
 *  - Filialpasswort (Kapitel 3.2, Screen 7) ist jetzt setz-/änderbar über
 *    `PUT /api/locations/:id/password` — nur das reine Setzen/Ändern, NICHT
 *    der volle alternative Login-Flow (Zwangs-Änderung beim ersten Login).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useCanManage, useCanManageLocation } from '../store/auth';
import { BUNDESLAND_LABELS, WEEKDAY_LABELS, WEEKDAY_ORDER, locationsApi } from '../lib/locations';
import { usersApi } from '../lib/users';
import { shiftsApi, type ApiShift } from '../lib/shifts';
import { calendarEntriesApi, ENTRY_TYPE_META, needsApproval, type ApiCalendarEntry } from '../lib/calendarEntries';
import { useBonusDecision } from '../data/shifts';
import { shiftHours } from '../lib/payroll';
import { formatDate, formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { companiesApi } from '../lib/companies';
import { regionsApi } from '../lib/regions';
import { ApiError } from '../lib/api';
import { Avatar } from '../components/Avatar';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconPlus,
  IconCalendar,
  IconTemplates,
  IconLock,
  IconUnlock,
  IconCheck,
} from '../components/icons';
import { UserFormModal } from './UsersAdminPage';
import { LocationModal } from './LocationsAdminPage';

const fmtH = (h: number) => `${h.toFixed(1).replace('.', ',')} Std.`;

/** Montag..Sonntag (ISO-Datum) der aktuellen Woche — wie backend/routes/dashboard.ts. */
function currentWeekRange(): { fromIso: string; toIso: string } {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = today.getUTCDay(); // 0=So..6=Sa
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { fromIso: monday.toISOString().slice(0, 10), toIso: sunday.toISOString().slice(0, 10) };
}

export default function LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const locationId = id ?? '';
  const navigate = useNavigate();
  const token = useAuth((s) => s.token)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const isOwner = useCanManage();
  const canManage = useCanManageLocation(locationId);
  const qc = useQueryClient();
  const bonusDecision = useBonusDecision();
  const { fromIso, toIso } = useMemo(() => currentWeekRange(), []);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditLocation, setShowEditLocation] = useState(false);
  // v1.3.0: Filialpasswort für den Chef wieder anzeigbar (zum Weitergeben).
  const [shownFilialPassword, setShownFilialPassword] = useState<string | null>(null);
  const [filialPasswordHint, setFilialPasswordHint] = useState<string | null>(null);
  const [filialPasswordBusy, setFilialPasswordBusy] = useState(false);
  const [filialPasswordCopied, setFilialPasswordCopied] = useState(false);
  async function revealFilialPassword() {
    setFilialPasswordBusy(true);
    setFilialPasswordHint(null);
    try {
      const r = await locationsApi.getPassword(token, locationId);
      if (r.password) {
        setShownFilialPassword(r.password);
      } else {
        setFilialPasswordHint(
          r.reason === 'not_set'
            ? 'Noch kein Filialpasswort gesetzt.'
            : 'Dieses Passwort wurde vor dem Update nur verschlüsselt-unumkehrbar gespeichert — einmal neu setzen, danach ist es hier jederzeit abrufbar.',
        );
      }
    } catch (err) {
      setFilialPasswordHint(err instanceof ApiError ? err.message : 'Passwort konnte nicht geladen werden.');
    } finally {
      setFilialPasswordBusy(false);
    }
  }
  async function copyFilialPassword() {
    if (!shownFilialPassword) return;
    try {
      await navigator.clipboard.writeText(shownFilialPassword);
      setFilialPasswordCopied(true);
      hapticSuccess();
      setTimeout(() => setFilialPasswordCopied(false), 2000);
    } catch {
      // Clipboard-API nicht verfügbar — Text ist markierbar (select-all).
    }
  }
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  function toggleExpand(userId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  const { data: location, isLoading } = useQuery({
    queryKey: ['location', locationId],
    queryFn: () => locationsApi.get(token, locationId),
    enabled: !!locationId && canManage,
  });

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(token),
    enabled: canManage,
  });
  const { data: regions } = useQuery({
    queryKey: ['regions'],
    queryFn: () => regionsApi.list(token),
    enabled: canManage,
  });
  const companyName = companies?.find((c) => c.id === location?.companyId)?.name;
  const regionName = regions?.find((r) => r.id === location?.regionId)?.name;

  const { data: users } = useQuery({
    queryKey: ['users', activeCompanyId],
    queryFn: () => usersApi.list(token),
    enabled: canManage,
  });
  // Chef (companyRole 'owner', bzw. Alt-'manager') und globale
  // Admins/Superadmins gehören organisatorisch nicht zum "Mitarbeiter"-
  // Begriff dieser Liste (Shadow, Feedback 08.09.2026) — Filialleitung
  // (LocationRole.manager) bleibt drin und wird weiterhin als solche
  // gekennzeichnet. Bugfix 08.09.2026 (Systemweiter Audit): Alt-'manager'
  // ergänzt — überall sonst im Code (`useCanManage`, `getCompanyOwnerUserIds`,
  // `isChefUser` in WeeklyPlanPage) zählt companyRole 'owner' UND das
  // historische 'manager' gleichermaßen als Chef; ohne das hier würde ein
  // Chef mit noch nicht migriertem Alt-Datensatz fälschlich als normaler
  // Mitarbeiter samt Lohn aufgeführt.
  const team = useMemo(
    () =>
      (users ?? []).filter(
        (u) =>
          u.locationMemberships.some((m) => m.locationId === locationId) &&
          u.role !== 'admin' &&
          u.role !== 'superadmin' &&
          !u.memberships.some(
            (m) => m.companyId === activeCompanyId && (m.role === 'owner' || m.role === 'manager'),
          ),
      ),
    [users, locationId, activeCompanyId],
  );
  const nameById = useMemo(() => new Map(team.map((u) => [u.id, u.name])), [team]);
  const teamIdSet = useMemo(() => new Set(team.map((u) => u.id)), [team]);

  const { data: weekShifts } = useQuery({
    queryKey: ['shifts', 'location-week', locationId, fromIso, toIso],
    queryFn: () => shiftsApi.list(token, { locationId, from: fromIso, to: toIso }),
    enabled: !!locationId && canManage,
  });
  // Bugfix 08.09.2026 (Systemweiter Audit): auf `team` gescoped (statt alle
  // `weekShifts` roh zu summieren) — sonst konnten "Mitarbeiter"- und
  // "Std. (Woche)"-Kachel auseinanderlaufen, sobald der (aus "Mitarbeiter"
  // ausgefilterte) Chef selbst eine Schicht an diesem Standort hatte: die
  // Stunden zählten dann trotzdem mit, ohne dass die Person in der Liste
  // auftauchte.
  const hoursThisWeek = useMemo(
    () =>
      Math.round(
        (weekShifts ?? [])
          .filter((s) => s.status !== 'cancelled' && teamIdSet.has(s.userId))
          .reduce((sum, s) => sum + shiftHours(s), 0) * 10,
      ) / 10,
    [weekShifts, teamIdSet],
  );

  // Montag..Sonntag der aktuellen Woche als ISO-Daten, für die
  // Stunden-pro-Tag-Aufklappung je Mitarbeiter (Shadow, Feedback 08.09.2026).
  const weekDays = useMemo(() => {
    const days: string[] = [];
    const start = new Date(`${fromIso}T00:00:00Z`);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  }, [fromIso]);
  const hoursByUserAndDay = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const s of weekShifts ?? []) {
      if (s.status === 'cancelled') continue;
      const perDay = map.get(s.userId) ?? new Map<string, number>();
      perDay.set(s.shiftDate, (perDay.get(s.shiftDate) ?? 0) + shiftHours(s));
      map.set(s.userId, perDay);
    }
    return map;
  }, [weekShifts]);

  const { data: pendingEntries } = useQuery({
    queryKey: ['calendar-entries', 'pending', activeCompanyId],
    queryFn: () => calendarEntriesApi.list(token, { status: 'pending' }),
    enabled: canManage,
  });
  const teamIds = useMemo(() => new Set(team.map((u) => u.id)), [team]);
  // Gesamtprüfung 11.09.2026: Anträge zählen beim HAUPT-Standort der Person
  // (dort wird auch entschieden, Kapitel 5.1) — wie die Kachel im
  // Chef-Dashboard. Ohne markierten Haupt-Standort: jeder ihrer Standorte.
  const primaryHereIds = useMemo(
    () =>
      new Set(
        team
          .filter((u) => {
            const own = u.locationMemberships.filter((m) => m.companyId === activeCompanyId);
            const primary = own.find((m) => m.isPrimary);
            return primary ? primary.locationId === locationId : true;
          })
          .map((u) => u.id),
      ),
    [team, activeCompanyId, locationId],
  );
  const myId = useAuth((s) => s.user?.id);
  const pendingAbsences = useMemo(
    () =>
      (pendingEntries ?? []).filter(
        (e) =>
          needsApproval(e.type) &&
          !e.deletedAt &&
          teamIds.has(e.userId) &&
          primaryHereIds.has(e.userId) &&
          // Eigene Anträge entscheidet eine Filialleitung nicht selbst.
          (isOwner || e.userId !== myId),
      ),
    [pendingEntries, teamIds, primaryHereIds, isOwner, myId],
  );

  // "Unbestätigte Einsprünge" (Kapitel 11.3/11.6) — nur der Chef entscheidet,
  // Filialleitung sieht dieses eigene Recht daher gar nicht erst (der
  // Backend-Filter liefert ihr für bonusStatus 'none' statt 'pending' zurück,
  // wenn managerCanSeeBonusHours an diesem Standort nicht gesetzt ist).
  const { data: unconfirmedShifts } = useQuery({
    queryKey: ['shifts', 'unconfirmed-einspringen', locationId],
    queryFn: () => shiftsApi.list(token, { locationId, origin: 'self_reported', bonusStatus: 'pending' }),
    enabled: !!locationId && isOwner,
  });

  async function decideAbsence(entry: ApiCalendarEntry, status: 'approved' | 'rejected') {
    try {
      await calendarEntriesApi.update(token, entry.id, { status }, entry.lastModified);
      hapticSuccess();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['calendar-entries', 'pending'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    } catch (err) {
      hapticError();
      alert(err instanceof Error ? err.message : 'Entscheidung fehlgeschlagen');
    }
  }

  async function decideBonus(shift: ApiShift, decision: 'approved' | 'rejected') {
    try {
      await bonusDecision.mutateAsync({ id: shift.id, decision, token });
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['shifts', 'unconfirmed-einspringen', locationId] });
    } catch {
      hapticError();
    }
  }

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center text-slate-400">
        <p>Kein Zugriff auf diesen Standort.</p>
      </div>
    );
  }

  if (isLoading || !location) {
    return <div className="max-w-md mx-auto mt-10 text-center text-slate-400">Lädt…</div>;
  }

  const templateSummary =
    location.templates && location.templates.length > 0
      ? `${location.templates.length} Vorlage${location.templates.length === 1 ? '' : 'n'} · ${location.templates
          .slice(0, 2)
          .map((t) => t.name)
          .join(', ')}${location.templates.length > 2 ? ' …' : ''}`
      : 'Noch keine Vorlagen angelegt.';

  return (
    <div className="max-w-md lg:max-w-2xl mx-auto space-y-6 pb-12">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="text-slate-300 hover:text-white shrink-0 !min-h-0 p-1 -ml-1"
          aria-label="Zurück zum Dashboard"
        >
          <IconChevronLeft width={18} height={18} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[15px] font-semibold truncate">{location.name}</h1>
          <p className="text-[11px] text-slate-400 truncate">
            {location.branchNumber ? `Nr. ${location.branchNumber}` : ''}
            {location.branchNumber && companyName ? ' · ' : ''}
            {companyName ?? ''}
          </p>
        </div>
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: location.isActive ? location.color : '#5b6b82' }}
          aria-hidden
        />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {/* v1.3.0: nur aktive Personen zählen — dieselbe Definition wie die Kachel im Chef-Dashboard. */}
        <Kpi label="Mitarbeiter" value={String(team.filter((u) => u.isActive).length)} />
        <Kpi label="Std. (Woche)" value={fmtH(hoursThisWeek)} />
        <Kpi label="Urlaubsanträge" value={String(pendingAbsences.length)} highlight />
      </div>

      <Section
        title="Mitarbeiter"
        action={
          <button
            onClick={() => setShowCreateUser(true)}
            className="btn-ghost text-[12.5px] px-2.5 py-1.5 !min-h-0 inline-flex items-center gap-1.5"
          >
            <IconPlus width={12} height={12} strokeWidth={2.2} />
            anlegen
          </button>
        }
      >
        {team.length === 0 ? (
          <p className="text-sm text-slate-500 px-1">Noch keine Mitarbeiter zugeordnet.</p>
        ) : (
          <div className="card divide-y divide-border/60">
            {team.map((u) => {
              const membership = u.locationMemberships.find((m) => m.locationId === locationId);
              const isManager = membership?.role === 'manager';
              const isExpanded = expandedIds.has(u.id);
              const perDay = hoursByUserAndDay.get(u.id);
              return (
                <div key={u.id}>
                  <button
                    onClick={() => toggleExpand(u.id)}
                    className="w-full p-3 flex items-center gap-3 text-left !min-h-0"
                    aria-expanded={isExpanded}
                  >
                    <Avatar name={u.name} manager={isManager} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <span
                        className={`badge ${isManager ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-500/25 text-slate-200'}`}
                      >
                        {isManager ? 'Filialleitung' : 'Mitarbeiter'}
                      </span>
                      {!u.isActive && (
                        <span className="badge bg-slate-600/60 text-slate-300 ml-1">Deaktiviert</span>
                      )}
                    </div>
                    <span className="text-[13px] font-mono text-slate-400 shrink-0">
                      {u.hourlyWage !== null ? `${formatMoney(u.hourlyWage)}/h` : '–'}
                    </span>
                    <IconChevronDown
                      width={14}
                      height={14}
                      className={`text-slate-500 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 pl-[54px]">
                      <div className="rounded-lg bg-slate-900/40 divide-y divide-border/40">
                        {weekDays.map((day, idx) => {
                          const h = perDay?.get(day) ?? 0;
                          const weekdayKey = WEEKDAY_ORDER[idx] ?? 'mon';
                          return (
                            <div key={day} className="px-3 py-1.5 flex items-center justify-between text-xs">
                              <span className="text-slate-400">
                                {WEEKDAY_LABELS[weekdayKey]} · {formatDate(day)}
                              </span>
                              <span className={h > 0 ? 'text-slate-200 font-mono' : 'text-slate-600 font-mono'}>
                                {h > 0 ? fmtH(h) : '–'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Planung">
        <NavCard
          to={`/wochenplan?location=${locationId}`}
          icon={<IconCalendar width={18} height={18} />}
          title="Schichtplan"
          desc="Wochenplan erstellen & veröffentlichen."
        />
        <NavCard
          to="/admin/locations"
          icon={<IconTemplates width={18} height={18} />}
          title="Vorlagen verwalten"
          desc={templateSummary}
        />
      </Section>

      <Section title="Urlaubspläne">
        {pendingAbsences.length === 0 ? (
          <p className="text-sm text-slate-500 px-1">Keine offenen Anträge.</p>
        ) : (
          <div className="card divide-y divide-border/60">
            {pendingAbsences.map((e) => {
              const meta = ENTRY_TYPE_META[e.type];
              return (
                <div key={e.id} className="p-3 flex items-center gap-3">
                  <Avatar name={nameById.get(e.userId) ?? '?'} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {nameById.get(e.userId) ?? '?'} · <span style={{ color: meta.color }}>{meta.label}</span>
                      {e.halfDay && <span className="text-xs text-slate-400"> · halber Tag</span>}
                    </p>
                    <p className="text-xs text-slate-400">{formatDate(e.date)}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => void decideAbsence(e, 'approved')}
                      className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2.5 py-1.5 !min-h-0"
                    >
                      Genehmigen
                    </button>
                    <button
                      onClick={() => void decideAbsence(e, 'rejected')}
                      className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2.5 py-1.5 !min-h-0"
                    >
                      Ablehnen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {isOwner && (unconfirmedShifts?.length ?? 0) > 0 && (
        <Section title="Unbestätigte Einsprünge">
          <div className="space-y-2">
            {(unconfirmedShifts ?? []).map((s) => (
              <div key={s.id} className="card p-3 flex flex-col gap-2 border-l-[3px] border-amber-400/70">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar name={nameById.get(s.userId) ?? '?'} />
                    <p className="text-[13.5px] font-medium truncate">
                      {nameById.get(s.userId) ?? 'Unbekannt'} ist spontan eingesprungen
                    </p>
                  </div>
                  <span className="badge bg-amber-500/20 text-amber-300 shrink-0">wartet auf dich</span>
                </div>
                <p className="text-xs text-slate-400">
                  {formatDate(s.shiftDate)} · {s.startTime}–{s.endTime}
                  {s.requestedBonusHours != null && s.requestedBonusHours > 0 && (
                    <> · davon {s.requestedBonusHours.toFixed(1).replace('.', ',')} Std. beantragte Zusatzstunden</>
                  )}
                </p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => void decideBonus(s, 'approved')}
                    disabled={bonusDecision.isPending}
                    className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2.5 py-1.5 !min-h-0 disabled:opacity-50"
                  >
                    Genehmigen
                  </button>
                  <button
                    onClick={() => void decideBonus(s, 'rejected')}
                    disabled={bonusDecision.isPending}
                    className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2.5 py-1.5 !min-h-0 disabled:opacity-50"
                  >
                    Ablehnen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Standort-Einstellungen">
        <div className="card divide-y divide-border/60">
          {location.address && <SettingsRow label="Adresse" value={location.address} />}
          {regionName && <SettingsRow label="Region" value={regionName} />}
          {location.bundesland && (
            <SettingsRow label="Bundesland" value={BUNDESLAND_LABELS[location.bundesland]} />
          )}
          <SettingsRow
            label="Filialleitung sieht Lohnsumme (€)"
            value={location.managerCanSeeWages ? 'An' : 'Aus'}
            on={location.managerCanSeeWages}
          />
          <SettingsRow
            label="Filialleitung sieht Zusatzstunden bei Einsprüngen"
            value={location.managerCanSeeBonusHours ? 'An' : 'Aus'}
            on={location.managerCanSeeBonusHours}
          />
          <SettingsRow
            label="Doppelschicht erlauben"
            value={location.allowDoubleShift ? 'An' : 'Aus'}
            on={location.allowDoubleShift}
          />
          <SettingsRow
            label="Standortübergreifenden Tausch erlauben"
            value={location.allowCrossLocationSwap ? 'An' : 'Aus'}
            on={location.allowCrossLocationSwap}
          />
          <div className="p-3 flex items-center justify-between gap-3">
            <span className="text-[13.5px] text-slate-200 max-w-[60%] inline-flex items-center gap-1.5">
              <IconLock width={13} height={13} className="text-slate-400 shrink-0" />
              Filialpasswort
            </span>
            {isOwner ? (
              <div className="flex items-center gap-1.5 shrink-0">
                {location.filialPasswordSet && !shownFilialPassword && (
                  <button
                    onClick={() => void revealFilialPassword()}
                    disabled={filialPasswordBusy}
                    className="btn-ghost text-xs px-2.5 py-1.5 !min-h-0 disabled:opacity-50"
                  >
                    {filialPasswordBusy ? '…' : 'anzeigen'}
                  </button>
                )}
                {shownFilialPassword && (
                  <button
                    onClick={() => setShownFilialPassword(null)}
                    className="btn-ghost text-xs px-2.5 py-1.5 !min-h-0"
                  >
                    verbergen
                  </button>
                )}
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="btn-ghost text-xs px-2.5 py-1.5 !min-h-0"
                >
                  {location.filialPasswordSet ? 'ändern' : 'setzen'}
                </button>
              </div>
            ) : (
              <span className="badge bg-slate-700 text-slate-400">
                {location.filialPasswordSet ? 'gesetzt' : 'nicht gesetzt'}
              </span>
            )}
          </div>
          {isOwner && shownFilialPassword && (
            <div className="p-3 flex items-center justify-between gap-3">
              <span className="font-mono text-[15px] tracking-wide text-slate-100 select-all break-all">
                {shownFilialPassword}
              </span>
              <button
                type="button"
                onClick={() => void copyFilialPassword()}
                className="btn-ghost text-xs px-2.5 py-1.5 !min-h-0 shrink-0"
              >
                {filialPasswordCopied ? 'Kopiert ✓' : 'Kopieren'}
              </button>
            </div>
          )}
          {isOwner && filialPasswordHint && (
            <p className="p-3 text-xs text-amber-300">{filialPasswordHint}</p>
          )}
        </div>
        {isOwner && (
          <button
            type="button"
            onClick={() => setShowEditLocation(true)}
            className="text-xs text-brand hover:underline px-1 inline-block !min-h-0"
          >
            Einstellungen bearbeiten →
          </button>
        )}
      </Section>

      {showCreateUser && (
        <UserFormModal
          mode="create"
          defaultLocationId={locationId}
          onClose={() => setShowCreateUser(false)}
          onSaved={() => {
            setShowCreateUser(false);
            // UsersAdminPage nutzt den Query-Key ['users'], DashboardPage/diese
            // Seite ['users', activeCompanyId] — beide invalidieren, sonst
            // bleibt eine der beiden Listen veraltet (bekannte Inkonsistenz).
            qc.invalidateQueries({ queryKey: ['users'] });
            qc.invalidateQueries({ queryKey: ['users', activeCompanyId] });
          }}
        />
      )}

      {showEditLocation && isOwner && (
        <LocationModal
          mode="edit"
          location={location}
          onClose={() => setShowEditLocation(false)}
          onSaved={() => {
            setShowEditLocation(false);
            qc.invalidateQueries({ queryKey: ['location', locationId] });
            qc.invalidateQueries({ queryKey: ['locations'] });
            qc.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      )}

      {showPasswordModal && (
        <FilialPasswordModal
          locationId={locationId}
          hasPassword={location.filialPasswordSet}
          token={token}
          onClose={() => setShowPasswordModal(false)}
          onSaved={() => {
            setShowPasswordModal(false);
            setShownFilialPassword(null);
            setFilialPasswordHint(null);
            qc.invalidateQueries({ queryKey: ['location', locationId] });
          }}
        />
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-xs uppercase tracking-wider text-slate-400">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function NavCard({ to, icon, title, desc }: { to: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Link to={to} className="card p-3.5 flex items-center gap-3 hover:bg-surface-2 transition !min-h-0">
      <span className="text-slate-400 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-slate-400 truncate">{desc}</p>
      </div>
      <IconChevronRight width={16} height={16} className="text-slate-500 shrink-0" />
    </Link>
  );
}

function Kpi({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="card p-2 sm:p-3 min-w-0">
      <p className="text-[11px] sm:text-xs text-slate-400 truncate">{label}</p>
      <p className={`text-base sm:text-lg font-semibold mt-0.5 truncate ${highlight ? 'text-amber-300' : ''}`}>
        {value}
      </p>
    </div>
  );
}

function SettingsRow({ label, value, on }: { label: string; value: string; on?: boolean }) {
  return (
    <div className="p-3 flex items-center justify-between gap-3">
      <span className="text-[13.5px] text-slate-200 max-w-[60%]">{label}</span>
      {on === undefined ? (
        <span className="text-[13px] text-slate-400 text-right">{value}</span>
      ) : (
        <span className={`badge ${on ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-700 text-slate-400'}`}>
          {value}
        </span>
      )}
    </div>
  );
}

/**
 * Filialpasswort setzen/ändern (Kapitel 3.2, Screen 7) — Nachtrag 08.09.2026.
 * Bewusst nur das Setzen/Ändern des Passworts selbst (Owner-only, wie
 * `PUT /api/locations/:id/password`). Der volle alternative Login-Flow für
 * Filial-Zugänge (Zwangs-Passwortänderung beim ersten Login etc.) ist NICHT
 * Teil dieser Umsetzung — dafür fehlt noch die Konzept-Abstimmung.
 *
 * Nachtrag 08.09.2026 (Shadow: "kurz sichtbar machen, damit man das
 * weitergeben kann"): das Passwort wird argon2id-gehasht gespeichert — ein
 * BEREITS gesetztes Passwort kann also grundsätzlich NIRGENDS mehr im
 * Klartext angezeigt werden, auch nicht nachträglich vom Backend. Die einzig
 * mögliche (und übliche, z. B. bei API-Keys) Lösung: direkt nach dem
 * Setzen/Ändern — während der Chef es noch selbst eingegeben hat — einmalig
 * gut sichtbar mit Kopieren-Button anzeigen, bevor das Modal schließt. Der
 * Klartext existiert ab dann nirgends mehr, weder im Frontend-State noch im
 * Backend.
 */
function FilialPasswordModal({
  locationId,
  hasPassword,
  token,
  onClose,
  onSaved,
}: {
  locationId: string;
  hasPassword: boolean;
  token: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Nach erfolgreichem Speichern: einmalige Klartext-Anzeige zum Weitergeben,
  // statt das Modal sofort zu schließen (siehe Kommentar oben).
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Mindestens 6 Zeichen.');
      return;
    }
    if (password !== confirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await locationsApi.setPassword(token, locationId, password);
      hapticSuccess();
      setRevealedPassword(password);
    } catch (err) {
      hapticError();
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Unbekannter Fehler');
    } finally {
      setBusy(false);
    }
  }

  async function copyPassword() {
    if (!revealedPassword) return;
    try {
      await navigator.clipboard.writeText(revealedPassword);
      setCopied(true);
      hapticSuccess();
    } catch {
      // Clipboard-API in manchem PWA-/Browser-Kontext nicht verfügbar —
      // dann bleibt Markieren + Kopieren aus dem sichtbaren Textfeld.
    }
  }

  // --- Schritt 2: einmalige Klartext-Anzeige nach erfolgreichem Speichern ---
  if (revealedPassword) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
        <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-1 inline-flex items-center gap-1.5">
            <IconCheck width={16} height={16} className="text-emerald-400" />
            Filialpasswort gespeichert
          </h3>
          <p className="text-xs text-slate-400 mb-4">
            Jetzt an die Filialleitung/das Team weitergeben. Du kannst es später jederzeit
            in den Standort-Einstellungen über „anzeigen" erneut abrufen (nur als Chef).
          </p>
          <div className="rounded-lg bg-slate-900/60 border border-border/60 p-3 flex items-center justify-between gap-3 mb-3">
            <span className="font-mono text-[15px] tracking-wide text-slate-100 select-all break-all">
              {revealedPassword}
            </span>
            <button
              type="button"
              onClick={() => void copyPassword()}
              className="btn-ghost text-xs px-2.5 py-1.5 !min-h-0 shrink-0"
            >
              {copied ? 'Kopiert ✓' : 'Kopieren'}
            </button>
          </div>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onSaved}
              className="px-3 py-2 text-sm rounded bg-brand hover:bg-brand-dark"
            >
              Fertig
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-1">
          Filialpasswort {hasPassword ? 'ändern' : 'setzen'}
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          Dieses Passwort ersetzt nicht die persönlichen Zugänge der Mitarbeiter — es ist ein
          zusätzlicher, standortweiter Zugang für die Erstanmeldung. Es wird verschlüsselt
          gespeichert; als Chef kannst du es in den Standort-Einstellungen jederzeit wieder
          anzeigen, um es weiterzugeben.
        </p>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Neues Passwort</span>
            <div className="relative">
              <input
                type={showPasswords ? 'text' : 'password'}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input pr-9"
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPasswords((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 !min-h-0 p-1"
                aria-label={showPasswords ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPasswords ? (
                  <IconUnlock width={15} height={15} />
                ) : (
                  <IconLock width={15} height={15} />
                )}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Wiederholen</span>
            <input
              type={showPasswords ? 'text' : 'password'}
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input"
              autoComplete="new-password"
            />
          </label>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-2 text-sm rounded bg-brand hover:bg-brand-dark disabled:opacity-50"
            >
              {busy ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
